import { Module } from "vuex";
import { clone, mergeObjects } from '../../../utils/general';
import { RootState } from '../../types';
import { SubscriptionState, MessageListSubscription, SourceQueryParams } from './types';
import { subscriptionActions } from './actions';
import { getChangedMessageListSubscriptions, getSourceMessageListSubscription } from './utils';

export const initialSubscriptionState = (): SubscriptionState => ({
  fanOid: null,
  // Current Subscription
  currentMessageListSubscriptions: [],
  fetchMessageListSubscriptionsError: null,
  isFetchingMessageListSubscriptions: false,
  // Scratch Subscription
  scratchMessageListSubscriptions: [],
  // Update Multiple Subscriptions
  isUpdateingMultipleMessageListSubscriptions: false,
  // Update subscription
  isUpdateingMessageListSubscription: false,

  // Sources
  sourceTaskOid: null,
  sourceMessageListOid: null,
  sourceChannel: null,
  sourceQueryParams: {},
});

export const subscriptionModule: Module<SubscriptionState, RootState> = {
  namespaced: true,
  state: initialSubscriptionState(),
  actions: subscriptionActions,
  mutations: {
    SET_FAN_OID(state, fanOid: number) {
      state.fanOid = fanOid;
    },
    // Current Subscription
    SET_CURRENT_MESSAGE_LIST_SUBSCRIPTIONS(state, messageListSubscriptions: MessageListSubscription[]) {
      state.currentMessageListSubscriptions = clone(messageListSubscriptions);
    },
    SET_IS_FETCHING_MESSAGE_LIST_SUBSCRIPTIONS(state, isFetching: boolean) {
      state.isFetchingMessageListSubscriptions = isFetching;
    },
    RESET_FETCH_MESSAGE_LIST_SUBSCRIPTIONS_ERROR(state) {
      state.fetchMessageListSubscriptionsError = null;
    },
    SET_FETCH_MESSAGE_LIST_SUBSCRIPTIONS_ERROR(state, error: { title: string, description: string }) {
      state.fetchMessageListSubscriptionsError = clone(error);
    },
    // Scratch Subscription
    SET_SCRATCH_MESSAGE_LIST_SUBSCRIPTIONS(state, messageListSubscriptions: MessageListSubscription[]) {
      state.scratchMessageListSubscriptions =
        clone(messageListSubscriptions.filter(s => !!s.userDefined && !!s.sysActivep));
    },
    TOGGLE_MESSAGE_LIST_SUBSCRIPTION(state, { messageListOid, channel, subscribed }: { messageListOid: number, channel: 'sms' | 'email' | 'facebookMessenger', subscribed: boolean }) {
      const clonedScratchMessageListSubscriptions: MessageListSubscription[] = clone(state.scratchMessageListSubscriptions);
      const targetMessageList = clonedScratchMessageListSubscriptions.find(list => list.oid === messageListOid);
      if (!targetMessageList) { return; }
      targetMessageList.subscription[channel] = subscribed;
      state.scratchMessageListSubscriptions = clonedScratchMessageListSubscriptions;
    },
    // Update Multiple Subscriptions
    SET_IS_UPDATING_MULTIPLE_MESSAGE_LIST_SUBSCRIPTIONS(state, isUpdating: boolean) {
      state.isUpdateingMultipleMessageListSubscriptions = isUpdating;
    },
    // Update Subscription
    SET_IS_UPDATING_MESSAGE_LIST_SUBSCRIPTION(state, isUpdating: boolean) {
      state.isUpdateingMessageListSubscription = isUpdating;
    },
    // Sources
    SET_SOURCE_TASK_OID(state, taskOid: number | null) {
      state.sourceTaskOid = taskOid;
    },
    SET_SOURCE_MESSAGE_LIST_OID(state, messageListOid: number | null) {
      state.sourceMessageListOid = messageListOid;
    },
    SET_SOURCE_CHANNEL(state, channel: 'sms' | 'email' | 'facebookMessenger' | null) {
      state.sourceChannel = channel;
    },
    SET_SOURCE_QUERY_PARAMS(state, sourceQueryParams: SourceQueryParams) {
      state.sourceQueryParams = clone(sourceQueryParams);
    },
  },
  getters: {
    changedScratchMessageListSubscriptions(state): MessageListSubscription[] {
      return getChangedMessageListSubscriptions(
        state.currentMessageListSubscriptions,
        state.scratchMessageListSubscriptions,
      );
    },
    sourceMessageListSubscription(state): MessageListSubscription | null {
      if (!state.sourceMessageListOid) {
        return null;
      }
      return getSourceMessageListSubscription(state.currentMessageListSubscriptions, state.sourceMessageListOid);
    },
  },
};

export default subscriptionModule;
